import React, { useState, useEffect } from "react";
import {
  MapPin,
  User,
  Clock,
  Layers,
  CheckCircle2,
  Pencil,
  Loader2,
} from "lucide-react";
import { post, put } from "../api/client";
import { HMPDashboardData } from "../types";
import { StatusPill, PriorityPill, BemiddelingPill } from "../pages/Vacatures";

// ── Types ──────────────────────────────────────────────────────────────────────

type StaleVacature = HMPDashboardData["stale_vacatures"][number];

interface InlineVacatureReviewProps {
  vacatures: HMPDashboardData["stale_vacatures"];
  onComplete: () => void; // called when all reviewed
}

// ── Edit form ──────────────────────────────────────────────────────────────────

interface EditFormState {
  status: string;
  priority: string;
  bemiddeling_status: string;
  contactpersoon: string;
  opmerkingen: string;
}

interface EditFormProps {
  vacature: StaleVacature;
  onSave: (data: EditFormState) => Promise<void>;
  onCancel: () => void;
}

function EditForm({ vacature, onSave, onCancel }: EditFormProps) {
  const [form, setForm] = useState<EditFormState>({
    status: vacature.status,
    priority: vacature.priority,
    bemiddeling_status: vacature.bemiddeling_status,
    contactpersoon: vacature.contactpersoon ?? "",
    opmerkingen: vacature.opmerkingen ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function setField<K extends keyof EditFormState>(key: K, value: EditFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await onSave(form);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3 pt-1">
      <div className="grid grid-cols-2 gap-3">
        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Status
          </label>
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
            className="select-field text-sm py-1.5"
            disabled={saving}
          >
            <option value="open">Open</option>
            <option value="gesloten">Gesloten</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>

        {/* Prioriteit */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Prioriteit
          </label>
          <select
            value={form.priority}
            onChange={(e) => setField("priority", e.target.value)}
            className="select-field text-sm py-1.5"
            disabled={saving}
          >
            <option value="hoog">Hoog</option>
            <option value="normaal">Normaal</option>
            <option value="laag">Laag</option>
          </select>
        </div>
      </div>

      {/* Bemiddeling */}
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Bemiddelingsstatus
        </label>
        <select
          value={form.bemiddeling_status}
          onChange={(e) => setField("bemiddeling_status", e.target.value)}
          className="select-field text-sm py-1.5"
          disabled={saving}
        >
          <option value="geen">Geen</option>
          <option value="in_bemiddeling">In Bemiddeling</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="geplaatst">Geplaatst</option>
        </select>
      </div>

      {/* Contactpersoon */}
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Contactpersoon
        </label>
        <input
          type="text"
          value={form.contactpersoon}
          onChange={(e) => setField("contactpersoon", e.target.value)}
          placeholder="Naam contactpersoon"
          className="input-field text-sm py-1.5"
          disabled={saving}
        />
      </div>

      {/* Opmerkingen */}
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Opmerkingen
        </label>
        <textarea
          value={form.opmerkingen}
          onChange={(e) => setField("opmerkingen", e.target.value)}
          placeholder="Aanvullende informatie…"
          rows={2}
          className="input-field text-sm py-1.5 resize-none"
          disabled={saving}
        />
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          disabled={saving}
          className="flex-1 btn-secondary text-sm py-2 min-h-[44px]"
        >
          Annuleren
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 btn-primary flex items-center justify-center gap-1.5 text-sm py-2 min-h-[44px]"
        >
          {saving ? (
            <>
              <Loader2 size={13} className="animate-spin" />
              Opslaan…
            </>
          ) : (
            "Opslaan & Verder"
          )}
        </button>
      </div>
    </div>
  );
}

// ── Animation state ────────────────────────────────────────────────────────────

type SlideState = "in" | "idle" | "out";

// ── Main component ─────────────────────────────────────────────────────────────

export default function InlineVacatureReview({
  vacatures,
  onComplete,
}: InlineVacatureReviewProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideState, setSlideState] = useState<SlideState>("in");
  const [showEdit, setShowEdit] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Slide in on mount
  useEffect(() => {
    const t = setTimeout(() => setSlideState("idle"), 10);
    return () => clearTimeout(t);
  }, []);

  const total = vacatures.length;
  const current = vacatures[currentIndex];

  // Advance to next card with slide animation
  function advance() {
    setShowEdit(false);
    setSlideState("out");
    setTimeout(() => {
      const next = currentIndex + 1;
      if (next >= total) {
        setDone(true);
        onComplete();
      } else {
        setCurrentIndex(next);
        setSlideState("in");
        setTimeout(() => setSlideState("idle"), 10);
      }
    }, 280);
  }

  async function handleConfirm() {
    if (confirmLoading) return;
    setConfirmLoading(true);
    try {
      await post(`/vacatures/${current.id}/review`, {});
    } catch {
      // Swallow — still advance (review mark is best-effort)
    } finally {
      setConfirmLoading(false);
      advance();
    }
  }

  async function handleEditSave(data: EditFormState) {
    await put(`/vacatures/${current.id}`, {
      status: data.status,
      priority: data.priority,
      bemiddeling_status: data.bemiddeling_status,
      contactpersoon: data.contactpersoon.trim() || undefined,
      opmerkingen: data.opmerkingen.trim() || undefined,
    });
    advance();
  }

  // ── Slide transform ────────────────────────────────────────────────────────
  const slideStyle: React.CSSProperties = {
    transition: "transform 280ms ease, opacity 280ms ease",
    transform:
      slideState === "in"
        ? "translateX(60px)"
        : slideState === "out"
        ? "translateX(-60px)"
        : "translateX(0)",
    opacity: slideState === "idle" ? 1 : 0,
  };

  // ── Done / success state ───────────────────────────────────────────────────
  if (done) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-5 md:p-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 size={28} className="text-white" />
        </div>
        <div>
          <div className="font-bold text-green-800">Alles up-to-date</div>
          <div className="text-sm text-green-700">Geen vacatures te reviewen</div>
        </div>
      </div>
    );
  }

  // ── Main review card ───────────────────────────────────────────────────────
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header: count + progress */}
      <div className="px-4 md:px-6 pt-4 md:pt-5 pb-3 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-slate-700">
            {total} vacature{total === 1 ? "" : "s"} te reviewen
          </span>
          <span className="text-xs font-medium text-slate-400">
            {currentIndex + 1} van {total}
          </span>
        </div>
        {/* Progress bar */}
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${((currentIndex + 1) / total) * 100}%`,
              backgroundColor: "#65ADDD",
            }}
          />
        </div>
      </div>

      {/* Card content */}
      <div style={slideStyle}>
        <div className="px-4 md:px-6 pt-4 pb-2">
          {/* Title */}
          <h3 className="text-lg font-bold text-slate-900 mb-4 leading-tight">
            {current.functie}
          </h3>

          {/* Detail rows */}
          <div className="space-y-2.5 mb-4">
            <div className="flex items-center gap-2.5 text-sm text-slate-600">
              <MapPin size={15} className="flex-shrink-0 text-slate-400" />
              <span>{current.locatie || "Locatie onbekend"}</span>
            </div>
            {current.contactpersoon && (
              <div className="flex items-center gap-2.5 text-sm text-slate-600">
                <User size={15} className="flex-shrink-0 text-slate-400" />
                <span>{current.contactpersoon}</span>
              </div>
            )}
            <div className="flex items-center gap-2.5 text-sm text-slate-600">
              <Layers size={15} className="flex-shrink-0 text-slate-400" />
              <span>
                {current.aantal_posities}{" "}
                {current.aantal_posities === 1 ? "positie" : "posities"}
              </span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-slate-600">
              <Clock size={15} className="flex-shrink-0 text-slate-400" />
              <span>
                <strong className="text-slate-700">
                  {current.days_since_update} dagen
                </strong>{" "}
                niet bijgewerkt
              </span>
            </div>
          </div>

          {/* Pills */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <StatusPill value={current.status} />
            <PriorityPill value={current.priority} />
            <BemiddelingPill value={current.bemiddeling_status} />
          </div>

          {/* Opmerkingen */}
          {current.opmerkingen && !showEdit && (
            <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs font-medium text-slate-500 mb-1">
                Opmerkingen
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">
                {current.opmerkingen}
              </p>
            </div>
          )}

          {/* Inline edit form */}
          {showEdit && (
            <div className="bg-slate-50 rounded-xl px-4 py-4 mb-4">
              <p className="text-xs font-semibold text-slate-600 mb-3 uppercase tracking-wide">
                Vacature bijwerken
              </p>
              <EditForm
                vacature={current}
                onSave={handleEditSave}
                onCancel={() => setShowEdit(false)}
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!showEdit && (
          <div className="px-4 md:px-6 pb-4 md:pb-6 flex flex-col md:flex-row gap-3">
            <button
              onClick={handleConfirm}
              disabled={confirmLoading}
              className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-150 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
            >
              {confirmLoading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Even wachten…
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Nog actueel ✓
                </>
              )}
            </button>
            <button
              onClick={() => setShowEdit(true)}
              disabled={confirmLoading}
              className="flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-150 border-2 text-waves-blue hover:bg-waves-blue-light disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px]"
              style={{ borderColor: "#65ADDD" }}
            >
              <Pencil size={15} />
              Bewerken ✏
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
